import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import api from '@/services/apiService';
import communityService from '@/services/communityService';
import showToast from '@/components/ui/Toast';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';

const ALL_SPORTS = [
  { id: 'futsal', label: 'Futsal', icon: 'ph:soccer-ball-bold' },
  { id: 'mini-soccer', label: 'Mini Soccer', icon: 'ph:soccer-ball-bold' },
  { id: 'sepak-bola', label: 'Sepak Bola', icon: 'ph:soccer-ball-bold' },
  { id: 'badminton', label: 'Badminton', icon: 'ph:tennis-ball-bold' },
  { id: 'padel', label: 'Padel', icon: 'ph:tennis-ball-bold' },
  { id: 'tenis', label: 'Tenis', icon: 'ph:tennis-ball-bold' },
  { id: 'basket', label: 'Basket', icon: 'ph:basketball-bold' },
  { id: 'voli', label: 'Voli', icon: 'ph:volleyball-bold' },
];

const ALL_DAYS = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

const formatToInputDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return '';
};

const normalizeSportLabel = (str: string): string => {
  if (!str) return '';
  const trimmed = str.trim();
  const found = ALL_SPORTS.find(
    (s) => s.label.toLowerCase() === trimmed.toLowerCase() || s.id.toLowerCase() === trimmed.toLowerCase()
  );
  return found ? found.label : trimmed;
};

const formatScheduleSentence = (days: string[]): string => {
  if (!days || days.length === 0) return '';
  // Sort days according to natural order in ALL_DAYS
  const sorted = [...days].sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b));

  if (sorted.length === 1) return sorted[0];
  if (sorted.length === 2) return `${sorted[0]} & ${sorted[1]}`;

  const allButLast = sorted.slice(0, -1).join(', ');
  const last = sorted[sorted.length - 1];
  return `${allButLast} & ${last}`;
};

const parseScheduleDays = (schedStr?: string): string[] => {
  if (!schedStr) return [];
  const rawDays = schedStr.split(/[,&]/).map((d: string) => d.trim()).filter(Boolean);
  const matched = rawDays
    .map((d) => ALL_DAYS.find((day) => day.toLowerCase() === d.toLowerCase()))
    .filter((day): day is string => Boolean(day));
  return Array.from(new Set(matched));
};

export const EditCommunityInfoPage: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [profileRaw, setProfileRaw] = useState<any>(null);

  // Form Fields - Defaults to empty if information in DB has no value
  const [name, setName] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [location, setLocation] = useState<string>('');
  const [foundedDate, setFoundedDate] = useState<string>('');
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [homeVenue, setHomeVenue] = useState<string>('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/community/profile');
        if (res.data?.data) {
          const comm = res.data.data.community || res.data.data;
          setProfileRaw(comm);

          setName(comm.name || comm.community_user?.name || '');
          setUsername(comm.slug || '');
          setLocation(comm.location || '');

          // Information object from DB
          const info = comm.information || {};

          // Sport categories normalization
          let rawSportsList: string[] = [];
          if (Array.isArray(info.selected_sports) && info.selected_sports.length > 0) {
            rawSportsList = info.selected_sports;
          } else if (info.sport_category_display) {
            rawSportsList = info.sport_category_display.split('·');
          } else if (comm.sport_category || comm.sport_category_r?.name) {
            const str = comm.sport_category || comm.sport_category_r?.name || '';
            rawSportsList = str.split('·');
          }

          const cleanedSports = Array.from(
            new Set(rawSportsList.map((s) => normalizeSportLabel(s)).filter(Boolean))
          );
          setSelectedSports(cleanedSports);

          setFoundedDate(formatToInputDate(info.founded_date));
          setHomeVenue(info.home_venue || '');

          const schedStr = info.regular_schedule || info.schedule || '';
          setSelectedDays(parseScheduleDays(schedStr));
        }
      } catch (err) {
        console.error('Failed to load profile data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const toggleSport = (sportLabel: string) => {
    setSelectedSports((prev) =>
      prev.includes(sportLabel)
        ? prev.filter((s) => s !== sportLabel)
        : [...prev, sportLabel]
    );
  };

  const toggleDay = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast.error('Nama komunitas wajib diisi');
      return;
    }

    setSubmitting(true);
    try {
      // Find valid slug for foreign key constraint (fk_sport_category_community)
      const primarySportObj = ALL_SPORTS.find((s) => selectedSports.includes(s.label));
      const primarySportSlug = primarySportObj?.id || 'futsal';
      const formattedSchedule = formatScheduleSentence(selectedDays);

      const payload = {
        name,
        slug: username,
        location,
        sport_category: primarySportSlug,
        description: profileRaw?.description || '',
        is_public: profileRaw?.is_public ?? true,
        information: {
          founded_date: foundedDate,
          regular_schedule: formattedSchedule,
          home_venue: homeVenue,
          sport_category_display: selectedSports.join(' · ') || 'Futsal',
          selected_sports: selectedSports,
        },
        keep_assets: {
          logo: profileRaw?.assets?.logo || undefined,
          cover: profileRaw?.assets?.cover_image || profileRaw?.assets?.cover || undefined,
          gallery: profileRaw?.assets?.gallery || [],
        },
      };

      await communityService.updateProfile(payload);
      
      // Also sync to dedicated /community/information endpoint
      try {
        await api.put('/community/information', {
          founded_date: foundedDate,
          regular_schedule: formattedSchedule,
          home_venue: homeVenue,
          sport_category_display: selectedSports.join(' · '),
          selected_sports: selectedSports,
        });
      } catch (infoErr) {
        console.warn('Dedicated information update warning:', infoErr);
      }

      showToast.success('Info dasar komunitas berhasil diperbarui!');

      // Update local storage
      localStorage.setItem('communityName', name);
      localStorage.setItem('communityNickname', username);

      navigate(-1);
    } catch (err: any) {
      console.error('Failed to save community info:', err);
      showToast.error(err?.response?.data?.message || 'Gagal menyimpan perubahan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <Icon icon="ph:spinner-gap-bold" className="w-8 h-8 text-[#FC480D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-start p-0 sm:p-4 text-[#0E1726]">
      {/* ========================================================================= */}
      {/* 📱 MOBILE VIEW (< md) */}
      {/* ========================================================================= */}
      <main className="w-full max-w-md sm:rounded-3xl shadow-xl overflow-hidden px-4 pt-4 pb-24 space-y-5 md:hidden">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* 📝 FIELD: NAMA KOMUNITAS */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400">
              Nama komunitas
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Masukkan nama komunitas"
              className="bg-white rounded-2xl border-gray-200 px-4 py-3 h-12 text-sm font-semibold text-[#0E1726] shadow-2xs focus-visible:ring-1 focus-visible:ring-[#FC480D]"
            />
          </div>

          {/* 👤 FIELD: USERNAME */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400">
              Username
            </label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder="garudamuda"
              className="bg-white rounded-2xl border-gray-200 px-4 py-3 h-12 text-sm font-semibold text-[#0E1726] shadow-2xs focus-visible:ring-1 focus-visible:ring-[#FC480D]"
            />
          </div>

          {/* ⚽ FIELD: KATEGORI OLAHRAGA */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-400">
              Kategori olahraga
            </label>
            <div className="flex flex-wrap gap-2">
              {ALL_SPORTS.map((sport) => {
                const isSelected = selectedSports.includes(sport.label);
                return (
                  <button
                    key={sport.id}
                    type="button"
                    onClick={() => toggleSport(sport.label)}
                    className={`px-3.5 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-[#FC480D] text-white shadow-xs'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon icon={sport.icon} className="w-3.5 h-3.5" />
                    <span>{sport.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 📍 FIELD: LOKASI */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400">
              Lokasi
            </label>
            <div className="relative">
              <Input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Pilih atau ketik lokasi"
                className="bg-white rounded-2xl border-gray-200 pl-10 pr-10 py-3 h-12 text-sm font-semibold text-[#0E1726] shadow-2xs focus-visible:ring-1 focus-visible:ring-[#FC480D]"
              />
              <Icon
                icon="ph:map-pin-bold"
                className="w-5 h-5 text-[#FC480D] absolute left-3.5 top-1/2 -translate-y-1/2"
              />
              <Icon
                icon="ph:caret-right-bold"
                className="w-4 h-4 text-gray-300 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
              />
            </div>
          </div>

          {/* ℹ️ SECTION HEADER: INFO KOMUNITAS */}
          <div className="pt-2">
            <h3 className="text-base font-extrabold text-[#0E1726]">
              Info komunitas
            </h3>
          </div>

          {/* 📅 FIELD: BERDIRI SEJAK */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400">
              Berdiri sejak
            </label>
            <div className="relative">
              <Input
                type="date"
                value={foundedDate}
                onChange={(e) => setFoundedDate(e.target.value)}
                className="bg-white rounded-2xl border-gray-200 pl-10 pr-4 py-3 h-12 text-sm font-semibold text-[#0E1726] shadow-2xs focus-visible:ring-1 focus-visible:ring-[#FC480D] [color-scheme:light] [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:top-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
              <Icon
                icon="ph:calendar-blank-bold"
                className="w-5 h-5 text-[#FC480D] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
              />
            </div>
          </div>

          {/* 🗓️ FIELD: JADWAL RUTIN */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-400">
              Jadwal rutin
            </label>
            <div className="flex flex-wrap gap-2">
              {ALL_DAYS.map((day) => {
                const isSelected = selectedDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-[#FC480D] text-white shadow-xs'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 🏟️ FIELD: HOME VENUE */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400">
              Home venue
            </label>
            <Input
              type="text"
              value={homeVenue}
              onChange={(e) => setHomeVenue(e.target.value)}
              placeholder="Masukkan nama venue"
              className="bg-white rounded-2xl border-gray-200 px-4 py-3 h-12 text-sm font-semibold text-[#0E1726] shadow-2xs focus-visible:ring-1 focus-visible:ring-[#FC480D]"
            />
          </div>

          {/* STICKY BOTTOM BUTTON: SIMPAN PERUBAHAN */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-150 z-30 flex justify-center sm:static sm:bg-transparent sm:border-0 sm:p-0 sm:mt-6">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full max-w-md h-12 bg-[#FC480D] hover:bg-[#e03e09] text-white font-extrabold text-sm rounded-full shadow-md transition-all active:scale-98 cursor-pointer disabled:opacity-50 gap-2"
            >
              {submitting ? (
                <>
                  <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <span>Simpan perubahan</span>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* ========================================================================= */}
      {/* 💻 DESKTOP VIEW (>= md) */}
      {/* ========================================================================= */}
      <main className="hidden md:block w-full max-w-4xl mx-auto space-y-6 pb-12">
        {/* DESKTOP HEADER & BREADCRUMB */}
        <div className="bg-white p-6 rounded-3xl border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-[#0E1726] hover:bg-gray-50 transition-all cursor-pointer"
            >
              <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-extrabold text-[#0E1726]">Edit Informasi Komunitas</h1>
              <p className="text-xs text-gray-400 mt-0.5 font-medium">
                Perbarui nama, username, cabang olahraga, serta info operasional komunitas.
              </p>
            </div>
          </div>
        </div>

        {/* DESKTOP FORM CARD */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-[#E0E6ED] p-8 shadow-2xs space-y-8">
          {/* SECTION 1: IDENTITAS UTAMA */}
          <div className="space-y-4">
            <h2 className="text-base font-extrabold text-[#0E1726] border-b border-gray-100 pb-3 flex items-center gap-2">
              <Icon icon="ph:user-bold" className="w-5 h-5 text-[#FC480D]" />
              <span>Identitas Komunitas</span>
            </h2>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Nama Komunitas</label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Masukkan nama komunitas"
                  className="bg-white rounded-2xl border-gray-200 px-4 py-3 h-12 text-sm font-semibold text-[#0E1726] focus-visible:ring-1 focus-visible:ring-[#FC480D]"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Username / Handle (@slug)</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="garudamuda"
                  className="bg-white rounded-2xl border-gray-200 px-4 py-3 h-12 text-sm font-semibold text-[#0E1726] focus-visible:ring-1 focus-visible:ring-[#FC480D]"
                />
              </div>
            </div>

            {/* KATEGORI OLAHRAGA */}
            <div className="space-y-2 pt-2">
              <label className="text-xs font-bold text-gray-700 block">Cabang Olahraga yang Dimainkan</label>
              <div className="flex flex-wrap gap-2.5">
                {ALL_SPORTS.map((sport) => {
                  const isSelected = selectedSports.includes(sport.label);
                  return (
                    <button
                      key={sport.id}
                      type="button"
                      onClick={() => toggleSport(sport.label)}
                      className={`px-4 py-2.5 rounded-full text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-[#FC480D] text-white shadow-xs'
                          : 'bg-[#F8FAFC] text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      <Icon icon={sport.icon} className="w-4 h-4" />
                      <span>{sport.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* SECTION 2: OPERASIONAL & INFORMASI DASHBOARD */}
          <div className="space-y-4 pt-2">
            <h2 className="text-base font-extrabold text-[#0E1726] border-b border-gray-100 pb-3 flex items-center gap-2">
              <Icon icon="ph:info-bold" className="w-5 h-5 text-[#FC480D]" />
              <span>Operasional & Detail Komunitas</span>
            </h2>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Kota / Lokasi Utama</label>
                <div className="relative">
                  <Input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Pilih atau ketik lokasi"
                    className="bg-white rounded-2xl border-gray-200 pl-10 pr-4 py-3 h-12 text-sm font-semibold text-[#0E1726] focus-visible:ring-1 focus-visible:ring-[#FC480D]"
                  />
                  <Icon
                    icon="ph:map-pin-bold"
                    className="w-5 h-5 text-[#FC480D] absolute left-3.5 top-1/2 -translate-y-1/2"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Berdiri Sejak</label>
                <div className="relative">
                  <Input
                    type="date"
                    value={foundedDate}
                    onChange={(e) => setFoundedDate(e.target.value)}
                    className="bg-white rounded-2xl border-gray-200 pl-10 pr-4 py-3 h-12 text-sm font-semibold text-[#0E1726] focus-visible:ring-1 focus-visible:ring-[#FC480D] [color-scheme:light] [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:top-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Icon
                    icon="ph:calendar-blank-bold"
                    className="w-5 h-5 text-[#FC480D] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Home Venue / Lapangan Utama</label>
                <Input
                  type="text"
                  value={homeVenue}
                  onChange={(e) => setHomeVenue(e.target.value)}
                  placeholder="Masukkan nama venue"
                  className="bg-white rounded-2xl border-gray-200 px-4 py-3 h-12 text-sm font-semibold text-[#0E1726] focus-visible:ring-1 focus-visible:ring-[#FC480D]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 block">Jadwal Rutin Main</label>
                <div className="flex flex-wrap gap-2">
                  {ALL_DAYS.map((day) => {
                    const isSelected = selectedDays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={`px-3.5 py-2 rounded-full text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-[#FC480D] text-white shadow-xs'
                            : 'bg-[#F8FAFC] text-gray-600 border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* DESKTOP ACTIONS FOOTER */}
          <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-12 px-6 rounded-full border border-gray-300 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-all cursor-pointer"
            >
              Batal
            </button>

            <Button
              type="submit"
              disabled={submitting}
              className="h-12 px-8 bg-[#FC480D] hover:bg-[#e03e09] text-white font-extrabold text-sm rounded-full shadow-md transition-all active:scale-98 cursor-pointer disabled:opacity-50 gap-2"
            >
              {submitting ? (
                <>
                  <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <span>Simpan Perubahan</span>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EditCommunityInfoPage;
